import Flex from "../../../../../../../../components/Flex";
import Text from "../../../../../../../../components/Text";
import ActionableHoverNew from "../ActionableHoverNew";
import { RightAction } from "../PatientRecords";
import { VaccineOptions } from "./SummaryConstant";

import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import CustomTooltip from "../../../../../../../../components/CustomTooltip";
import { useRoleController } from "../../../../../../../../components/useRoleController";
import useDisclosure from "../../../../../../../../hooks/use-disclosure";
import { useQueryParams } from "../../../../../../../../hooks/use-query-param";
import { useSnackbar } from "../../../../../../../../hooks/useSnackbar";
import { useClinicalSummaryLink } from "../../../../../../../../queries/encounter/use-clinical-summary-link";
import { ClinicalSummaryLinkRequestClinicalSummaryTypeEnum } from "../../../../../../../../services";
import {
  DEFAULT_KEY_LABEL_COLOR,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../../../../utils/utils";
import UpsertVaccineDrawer from "../../vaccines/UpsertVaccineDrawer";
import { VaccinesTooltip } from "./encounterTooltips/VaccinesTooltip";
import DeleteModal from "../../../../../../../../components/app/DeleteModal";
import { useArchiveState } from "../../../../../../../../hooks/useArchiveState";
import { useArchiveVaccineMutation } from "../../../../../../../../queries/vaccines/use-patient-vaccine-archive";
import LoadingAndEmptyStateWrapper from "./commonWrappers/LoadingAndEmptyStateWrapper";
import { useRecordsScroll } from "./RecordsScrollContext";

interface props {
  vaccinesList: any;
  loading: boolean;
}

const VaccinesRecord = ({ vaccinesList, loading }: props) => {
  const { mutateAsync: archiveVaccine } = useArchiveVaccineMutation();
  const { archiveHandler, deleteDisc } = useArchiveState(archiveVaccine);
  const upsertVaccineDisc = useDisclosure();
  const [disabledTooltips, setDisabledTooltips] = useState<{
    [key: number]: boolean;
  }>({});
  const { mutateAsync: linkClinicalDataToAppointment } =
    useClinicalSummaryLink();
  const queryClient = useQueryClient();
  const appointmentid = useQueryParams("appointmentid");
  const showSnackbar = useSnackbar();
  const { hasView } = useRoleController("Vaccine");
  const { isScrolling } = useRecordsScroll();

  const handleActionAreaToggle = (idx: number, isHovered: boolean) => {
    setDisabledTooltips((prev) => ({ ...prev, [idx]: isHovered }));
  };

  const handleMenuToggle = (idx: number, isOpen: boolean) => {
    setDisabledTooltips((prev) => ({ ...prev, [idx]: isOpen }));
  };

  const clickHandler = (v: string, idx: number) => {
    if (v === "Edit") {
      upsertVaccineDisc.open({
        ...vaccinesList?.data?.content[idx],
        vaccine: {
          value: vaccinesList?.data?.content[idx]?.vaccine?.uuid,
          label: vaccinesList?.data?.content[idx]?.vaccine?.name,
        },
        administeredBy: {
          value: Object.keys(
            vaccinesList?.data?.content[idx]?.administeredBy
          )[0],
          label: Object.values(
            vaccinesList?.data?.content[idx]?.administeredBy
          )[0],
        },
      });
    }
    if (v === "ExportToNote") {
      linkClinicalDataToAppointment({
        clinicalSummaryLinkRequest: {
          clinicalSummaryType:
            ClinicalSummaryLinkRequestClinicalSummaryTypeEnum.Vaccine,
          recordId: vaccinesList?.data?.content[idx]?.uuid,
          appointmentId: appointmentid ?? "",
        },
        xTENANTID: getXtenantId(),
      })
        .then((res) => {
          showSnackbar(getSuccessSnackbar(res));
          queryClient.invalidateQueries({ queryKey: ["patient-vaccine-list"] });
        })
        .catch((err) => {
          showSnackbar(getErrorSnackbar(err));
        });
    }
    if (v === "delete") {
      deleteDisc.open(vaccinesList?.data?.content[idx]?.uuid);
    }
  };

  return (
    <>
      <DeleteModal
        w="380px"
        open={deleteDisc.opened}
        onClose={deleteDisc.close}
        id={deleteDisc.deleteId}
        onConfirmation={(id) =>
          archiveHandler(true, id).then((res) => {
            queryClient.invalidateQueries({
              queryKey: ["patient-vaccine-list"],
            });
            return res;
          })
        }
        content={`You want to Delete this Vaccine`}
      />
      <Flex flexDirection={"column"} width={"100%"} gap={1}>
        <UpsertVaccineDrawer disclosureHost={upsertVaccineDisc} />
        <LoadingAndEmptyStateWrapper
          loading={loading}
          isEmpty={vaccinesList?.data?.content?.length === 0}
          emptyMessage="No Vaccines found"
        />
        {vaccinesList?.data?.content?.map((it: any, idx: number) => {
          const isTooltipDisabled = disabledTooltips[idx] || false;
          return (
            <Flex key={idx} width={"100%"}>
              <CustomTooltip
                key={it?.vaccine?.number}
                content={<VaccinesTooltip key={idx} data={it} />}
                position="right"
                disableTooltip={isTooltipDisabled || isScrolling}
              >
                <ActionableHoverNew
                  renderAction={
                    <div
                      onMouseEnter={() => handleActionAreaToggle(idx, true)}
                      onMouseLeave={() => handleActionAreaToggle(idx, false)}
                    >
                      {hasView && (
                        <RightAction
                          handleClickOption={(v) => clickHandler(v, idx)}
                          menuList={VaccineOptions}
                          onMenuOpen={() => handleMenuToggle(idx, true)}
                          onMenuClose={() => handleMenuToggle(idx, false)}
                        />
                      )}
                    </div>
                  }
                >
                  <Flex flexDirection={"column"} width={"100%"} gap={2}>
                    <Text isTabletext color={DEFAULT_KEY_LABEL_COLOR}>
                      {/* {idx + 1}.{" "} */}
                      {/* {it?.expiryDate ? formatDate(it?.expiryDate) : ""}-{" "}
                      {it?.vaccine?.number}-{it?.vaccine?.name} */}
                      {idx + 1}. {it?.vaccine?.name}
                      {it?.doseDuration ? ` - ${it?.doseDuration}` : ""}
                    </Text>
                  </Flex>
                </ActionableHoverNew>
              </CustomTooltip>
            </Flex>
          );
        })}
      </Flex>
    </>
  );
};

export default VaccinesRecord;
